import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { TreatmentType, PhaseRatios } from '../../types';
import {
  generateCurvePoints,
  generateSVGPath,
  generateFilledPath,
  getPhaseSegments,
  getDataAtX,
  type CurvePoint,
} from '../../lib/curve-utils';
import {
  PHASE_COLORS_RGB,
  PHASE_LABELS,
  PHASE_DESCRIPTIONS,
  DEFAULT_DURATION_MINUTES,
} from '../../lib/constants';
import { formatMinutes } from '../../lib/playlist-generator';

interface HoveredTrackInfo {
  title: string;
  artist: string;
}

interface TreatmentCurveChartProps {
  treatmentType: TreatmentType;
  durationMinutes?: number;
  phaseRatios?: PhaseRatios;
  onHoverTimeChange?: (minutes: number | null) => void;
  hoveredTrack?: HoveredTrackInfo | null;
  externalHoverMinute?: number | null;
  glowEnabled?: boolean;
}

const WIDTH = 400;
const HEIGHT = 180;
const PADDING = { top: 20, bottom: 52, left: 12, right: 12 };
const ANIMATION_DURATION = 300;

function getTickInterval(sessionMinutes: number): number {
  if (sessionMinutes <= 30) return 5;
  if (sessionMinutes <= 45) return 10;
  if (sessionMinutes <= 90) return 15;
  return 30;
}

function generateTimeTicks(sessionMinutes: number): number[] {
  const interval = getTickInterval(sessionMinutes);
  const ticks: number[] = [];
  for (let t = 0; t <= sessionMinutes; t += interval) {
    ticks.push(t);
  }
  // Include endpoint if it's not too close to the last regular tick
  if (sessionMinutes % interval !== 0 && sessionMinutes - ticks[ticks.length - 1] > interval * 0.3) {
    ticks.push(sessionMinutes);
  }
  return ticks;
}

function formatTickLabel(minutes: number): string {
  if (minutes === 0) return '0m';
  if (minutes < 60) return `${Math.floor(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  if (m === 0) return `${h}h`;
  return `${h}:${String(m).padStart(2, '0')}m`;
}

export function TreatmentCurveChart({
  treatmentType,
  durationMinutes = DEFAULT_DURATION_MINUTES,
  phaseRatios,
  onHoverTimeChange,
  hoveredTrack,
  externalHoverMinute,
  glowEnabled = false,
}: TreatmentCurveChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverX, setHoverX] = useState<number | null>(null);
  const animationRef = useRef<number | null>(null);
  const isFirstRender = useRef(true);
  const sessionMinutes = durationMinutes > 0 ? durationMinutes : DEFAULT_DURATION_MINUTES;

  // Generate target curve points
  const targetPoints = useMemo(
    () => generateCurvePoints(treatmentType, 50, sessionMinutes, phaseRatios),
    [treatmentType, sessionMinutes, phaseRatios]
  );

  // Initialize animated points with lazy initializer (avoids setState in effect)
  const [animatedPoints, setAnimatedPoints] = useState<CurvePoint[]>(() =>
    generateCurvePoints(treatmentType, 50, sessionMinutes, phaseRatios)
  );
  const previousPointsRef = useRef<CurvePoint[]>(animatedPoints);

  // Derive animation state by comparing current to target (avoids setState in effect)
  const isAnimating = useMemo(() => {
    if (animatedPoints.length !== targetPoints.length) return true;
    // Check if any y values differ significantly (animation in progress)
    return animatedPoints.some((point, i) =>
      Math.abs(point.y - targetPoints[i].y) > 0.001
    );
  }, [animatedPoints, targetPoints]);

  // Phase segments for background regions
  const phaseSegments = useMemo(
    () => getPhaseSegments(treatmentType, phaseRatios),
    [treatmentType, phaseRatios]
  );

  // Animate curve when treatment type changes (skip first render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Cancel any ongoing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startPoints = previousPointsRef.current;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);

      // Ease out cubic
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      // Interpolate between start and target points
      const interpolated = targetPoints.map((target, i) => {
        const start = startPoints[i] || target;
        return {
          x: target.x,
          y: start.y + (target.y - start.y) * easeProgress,
          phase: target.phase,
          minutes: target.minutes,
        };
      });

      setAnimatedPoints(interpolated);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        previousPointsRef.current = targetPoints;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetPoints]);

  // Handle mouse move for hover
  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;

    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const svgX = (x / rect.width) * WIDTH;

    // Convert to normalized x (0-1)
    const usableWidth = WIDTH - PADDING.left - PADDING.right;
    const normalizedX = (svgX - PADDING.left) / usableWidth;

    if (normalizedX >= 0 && normalizedX <= 1) {
      setHoverX(normalizedX);
      // Calculate minutes from normalized x (uses session duration)
      const minutes = normalizedX * sessionMinutes;
      onHoverTimeChange?.(minutes);
    } else {
      setHoverX(null);
      onHoverTimeChange?.(null);
    }
  }, [onHoverTimeChange, sessionMinutes]);

  const handleMouseLeave = useCallback(() => {
    setHoverX(null);
    onHoverTimeChange?.(null);
  }, [onHoverTimeChange]);

  // Calculate external hover position (from playlist hover)
  const externalHoverX = useMemo(() => {
    if (externalHoverMinute === null || externalHoverMinute === undefined) return null;
    // Convert minutes to normalized x (0-1)
    return externalHoverMinute / sessionMinutes;
  }, [externalHoverMinute, sessionMinutes]);

  // Effective hover position: internal hover takes precedence over external
  const effectiveHoverX = hoverX !== null ? hoverX : externalHoverX;

  // Get hover data
  const hoverData = useMemo(() => {
    if (effectiveHoverX === null || animatedPoints.length === 0) return null;
    return getDataAtX(animatedPoints, effectiveHoverX);
  }, [effectiveHoverX, animatedPoints]);

  // Generate paths
  const curvePath = useMemo(
    () => generateSVGPath(animatedPoints, WIDTH, HEIGHT, PADDING),
    [animatedPoints]
  );

  const filledPath = useMemo(
    () => generateFilledPath(animatedPoints, WIDTH, HEIGHT, PADDING),
    [animatedPoints]
  );

  // Calculate hover position in SVG coords
  const hoverSvgX = useMemo(() => {
    if (effectiveHoverX === null) return null;
    const usableWidth = WIDTH - PADDING.left - PADDING.right;
    return PADDING.left + effectiveHoverX * usableWidth;
  }, [effectiveHoverX]);

  const hoverSvgY = useMemo(() => {
    if (!hoverData) return null;
    const usableHeight = HEIGHT - PADDING.top - PADDING.bottom;
    return PADDING.top + (1 - hoverData.y) * usableHeight;
  }, [hoverData]);

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full h-auto cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Gradient definitions */}
        <defs>
          <linearGradient id="curveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            {phaseSegments.map((segment) => (
              <stop
                key={segment.phase}
                offset={`${segment.startX * 100}%`}
                stopColor={`rgb(${PHASE_COLORS_RGB[segment.phase]})`}
                stopOpacity={0.6}
              />
            ))}
            <stop
              offset="100%"
              stopColor={`rgb(${PHASE_COLORS_RGB.closing})`}
              stopOpacity={0.6}
            />
          </linearGradient>
          <linearGradient id="curveGradientFill" x1="0%" y1="0%" x2="100%" y2="0%">
            {phaseSegments.map((segment) => (
              <stop
                key={segment.phase}
                offset={`${segment.startX * 100}%`}
                stopColor={`rgb(${PHASE_COLORS_RGB[segment.phase]})`}
                stopOpacity={0.15}
              />
            ))}
            <stop
              offset="100%"
              stopColor={`rgb(${PHASE_COLORS_RGB.closing})`}
              stopOpacity={0.15}
            />
          </linearGradient>
          {glowEnabled && (
            <filter id="curveGlow">
              <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          )}
        </defs>

        {/* Phase region backgrounds */}
        {phaseSegments.map((segment) => {
          const usableWidth = WIDTH - PADDING.left - PADDING.right;
          const x = PADDING.left + segment.startX * usableWidth;
          const width = (segment.endX - segment.startX) * usableWidth;

          return (
            <rect
              key={segment.phase}
              x={x}
              y={PADDING.top}
              width={width}
              height={HEIGHT - PADDING.top - PADDING.bottom}
              fill={`rgb(${PHASE_COLORS_RGB[segment.phase]})`}
              fillOpacity={0.05}
            />
          );
        })}

        {/* Filled area under curve */}
        {filledPath && (
          <path
            d={filledPath}
            fill="url(#curveGradientFill)"
            className={isAnimating ? '' : 'transition-all duration-300'}
          />
        )}

        {/* Main curve line */}
        {curvePath && (
          <path
            d={curvePath}
            fill="none"
            stroke="url(#curveGradient)"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={isAnimating ? '' : 'transition-all duration-300'}
            filter={glowEnabled ? 'url(#curveGlow)' : undefined}
            style={glowEnabled ? { animation: 'curve-glow-breathe 4s ease-in-out infinite' } : undefined}
          />
        )}

        {/* Phase labels at bottom (with overlap de-collision) */}
        {(() => {
          const usableWidth = WIDTH - PADDING.left - PADDING.right;
          const CHAR_WIDTH = 5.5; // approximate px per character at 9px font-medium
          const LABEL_GAP = 4; // minimum gap between labels

          // Only show text labels for the middle phases (not opening/closing)
          const middlePhases = phaseSegments.filter(
            (s) => s.phase !== 'opening' && s.phase !== 'closing'
          );

          const labels = middlePhases.map((segment) => {
            const label = PHASE_LABELS[segment.phase];
            const centerX = PADDING.left + ((segment.startX + segment.endX) / 2) * usableWidth;
            const textWidth = label.length * CHAR_WIDTH;
            return { phase: segment.phase, label, x: centerX, width: textWidth };
          });

          const iconSize = 8;
          const iconPad = iconSize + LABEL_GAP;
          const minX = PADDING.left + iconPad;
          const maxX = PADDING.left + usableWidth - iconPad;
          const labelAreaWidth = Math.max(0, maxX - minX);

          // Check if total label width exceeds available space
          const totalLabelWidth = labels.reduce((sum, l) => sum + l.width, 0)
            + LABEL_GAP * Math.max(0, labels.length - 1);
          const scaleFactor = totalLabelWidth > labelAreaWidth
            ? labelAreaWidth / totalLabelWidth
            : 1;
          if (scaleFactor < 1) {
            for (const l of labels) {
              l.width *= scaleFactor;
            }
          }

          // Forward pass: push right when overlapping
          for (let i = 0; i < labels.length; i++) {
            const halfW = labels[i].width / 2;
            labels[i].x = Math.max(minX + halfW, Math.min(maxX - halfW, labels[i].x));

            if (i < labels.length - 1) {
              const left = labels[i];
              const right = labels[i + 1];
              const minDist = (left.width + right.width) / 2 + LABEL_GAP;
              if (right.x - left.x < minDist) {
                right.x = left.x + minDist;
              }
            }
          }

          // Backward pass
          for (let i = labels.length - 1; i >= 0; i--) {
            const halfW = labels[i].width / 2;
            labels[i].x = Math.max(minX + halfW, Math.min(maxX - halfW, labels[i].x));

            if (i > 0) {
              const left = labels[i - 1];
              const right = labels[i];
              const minDist = (left.width + right.width) / 2 + LABEL_GAP;
              if (right.x - left.x < minDist) {
                left.x = right.x - minDist;
              }
            }
          }

          // Final clamp
          for (const l of labels) {
            const halfW = l.width / 2;
            l.x = Math.max(minX + halfW, Math.min(maxX - halfW, l.x));
          }

          const fontSize = scaleFactor < 1 ? Math.max(7, 9 * scaleFactor) : 9;
          const iconY = HEIGHT - 12;

          return (
            <>
              {/* Start icon — small right-pointing triangle at left edge */}
              <g opacity={0.45} data-testid="phase-start-icon">
                <polygon
                  points={`${PADDING.left + 1},${iconY - iconSize / 2} ${PADDING.left + 1},${iconY + iconSize / 2} ${PADDING.left + 1 + iconSize * 0.8},${iconY}`}
                  className="fill-parchment-500 dark:fill-white"
                />
              </g>

              {/* Middle phase text labels */}
              {labels.map(({ phase, label, x }) => (
                <text
                  key={phase}
                  x={x}
                  y={HEIGHT - 8}
                  textAnchor="middle"
                  className="fill-parchment-500 dark:fill-white/40 font-medium"
                  style={{ fontSize: `${fontSize}px` }}
                >
                  {label}
                </text>
              ))}

              {/* End icon — small square at right edge */}
              <g opacity={0.45} data-testid="phase-end-icon">
                <rect
                  x={PADDING.left + usableWidth - iconSize + 1}
                  y={iconY - iconSize / 2}
                  width={iconSize - 1}
                  height={iconSize}
                  rx={1.5}
                  className="fill-parchment-500 dark:fill-white"
                />
              </g>
            </>
          );
        })()}

        {/* Time axis */}
        {(() => {
          const usableWidth = WIDTH - PADDING.left - PADDING.right;
          const chartBottom = HEIGHT - PADDING.bottom;
          const ticks = generateTimeTicks(sessionMinutes);
          return (
            <>
              {/* Baseline */}
              <line
                x1={PADDING.left}
                y1={chartBottom}
                x2={PADDING.left + usableWidth}
                y2={chartBottom}
                className="stroke-slate-300 dark:stroke-white/15"
                strokeWidth={0.5}
              />
              {/* Tick marks and labels */}
              {ticks.map((t) => {
                const x = PADDING.left + (t / sessionMinutes) * usableWidth;
                return (
                  <g key={t}>
                    <line
                      x1={x}
                      y1={chartBottom}
                      x2={x}
                      y2={chartBottom + 4}
                      className="stroke-slate-400 dark:stroke-white/25"
                      strokeWidth={0.75}
                    />
                    <text
                      x={x}
                      y={chartBottom + 14}
                      textAnchor="middle"
                      className="fill-slate-400 dark:fill-white/30 text-[8px]"
                    >
                      {formatTickLabel(t)}
                    </text>
                  </g>
                );
              })}
            </>
          );
        })()}

        {/* Hover elements */}
        {hoverData && hoverSvgX !== null && hoverSvgY !== null && (
          <>
            {/* Vertical line */}
            <line
              x1={hoverSvgX}
              y1={PADDING.top}
              x2={hoverSvgX}
              y2={HEIGHT - PADDING.bottom}
              stroke="white"
              strokeOpacity={0.3}
              strokeWidth={1}
              strokeDasharray="4 2"
            />
            {/* Dot on curve */}
            <circle
              cx={hoverSvgX}
              cy={hoverSvgY}
              r={5}
              fill={`rgb(${PHASE_COLORS_RGB[hoverData.phase]})`}
              stroke="white"
              strokeWidth={2}
            />
          </>
        )}
      </svg>

      {/* Hover tooltip */}
      {hoverData && hoverSvgX !== null && (
        <Tooltip
          data={hoverData}
          svgRef={svgRef}
          svgX={hoverSvgX}
          hoveredTrack={hoveredTrack}
        />
      )}
    </div>
  );
}

interface TooltipProps {
  data: CurvePoint;
  svgRef: React.RefObject<SVGSVGElement | null>;
  svgX: number;
  hoveredTrack?: HoveredTrackInfo | null;
}

function Tooltip({ data, svgRef, svgX, hoveredTrack }: TooltipProps) {
  const [position, setPosition] = useState({ x: 0, showOnLeft: false });

  useEffect(() => {
    if (!svgRef.current) return;

    const rect = svgRef.current.getBoundingClientRect();
    const pixelX = (svgX / WIDTH) * rect.width;
    const showOnLeft = pixelX > rect.width * 0.6;

    setPosition({ x: pixelX, showOnLeft });
  }, [svgRef, svgX]);

  return (
    <div
      className={`absolute top-0 transform -translate-y-1/4 pointer-events-none z-10
        ${position.showOnLeft ? '-translate-x-full pr-3' : 'translate-x-0 pl-3'}`}
      style={{ left: position.x }}
    >
      <div className="bg-earth-900/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg border border-white/10">
        <div className="flex items-center gap-2 mb-1">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: `rgb(${PHASE_COLORS_RGB[data.phase]})` }}
          />
          <span className="text-white font-medium text-sm">
            {PHASE_LABELS[data.phase]}
          </span>
        </div>
        <p className="text-white/60 text-xs max-w-[180px] leading-relaxed">
          {PHASE_DESCRIPTIONS[data.phase]}
        </p>
        <p className="text-white/40 text-xs mt-1">
          ~{formatMinutes(data.minutes)} into session
        </p>
        {hoveredTrack && (
          <div className="mt-2 pt-2 border-t border-white/10">
            <p className="text-white text-xs font-medium truncate max-w-[180px]">{hoveredTrack.title}</p>
            <p className="text-white/60 text-xs truncate max-w-[180px]">{hoveredTrack.artist}</p>
          </div>
        )}
      </div>
    </div>
  );
}
